import { useCallback, useMemo } from 'react';
import { scheduleActions } from 'features/schedule/redux';
import { useAppDispatch } from 'shared/services/redux/hooks/use-dispatch';
import { formatSubjectWithLevel } from 'shared/helpers/utils/subject-display';
import { educationParallelTextRepresentation } from 'entities/education-class';
import {
  useGetEducationClassListQuery,
  useGetRoomsQuery,
  useGetSubjectsQuery,
  useGetTeacherWorkloadsQuery,
} from 'shared/api';
import type { ScheduleFilters } from 'shared/api';
import type { TableFilterField } from 'shared/UI/UIKit/TableFilter';
import { scheduleFilterFields } from './schedule-filter-fields';

export const useScheduleFilters = () => {
  const dispatch = useAppDispatch();

  const { data: teachersData } = useGetTeacherWorkloadsQuery({
    size: 10000,
  });
  const { data: educationClassesData } = useGetEducationClassListQuery({ size: 10000 });
  const { data: subjectsData, isFetching: subjectsLoading } = useGetSubjectsQuery({
    size: 10000,
    sort: ['name,asc'],
  });
  const { data: roomsData, isFetching: roomsLoading } = useGetRoomsQuery({ size: 10000 });

  const isLoading = subjectsLoading || roomsLoading;

  const teachersOptions = useMemo(
    () =>
      teachersData?.content.map((teacher) => ({
        value: teacher.id,
        label: teacher.code,
      })) || [],
    [teachersData],
  );

  const educationalClassesOptions = useMemo(
    () =>
      educationClassesData?.content.map((educationClass) => ({
        value: educationClass.id,
        label: `${educationParallelTextRepresentation[educationClass.parallel]}${
          educationClass.litera
        }`,
      })) || [],
    [educationClassesData],
  );

  const subjectsOptions = useMemo(
    () =>
      subjectsData?.content.map((subject) => ({
        value: subject.id,
        label: formatSubjectWithLevel(subject.name, subject.educationLevel),
      })) || [],
    [subjectsData],
  );

  const roomsOptions = useMemo(
    () =>
      roomsData?.content.map((room) => ({
        value: room.id,
        label: `${room.number} - ${room.name}`,
      })) || [],
    [roomsData],
  );

  const filterFields = useMemo<TableFilterField[]>(() => {
    return scheduleFilterFields.map((field) => {
      switch (field.name) {
        case 'teacherId':
          return { ...field, options: teachersOptions, loading: isLoading };
        case 'subjectIds':
          return { ...field, options: subjectsOptions, loading: isLoading };
        case 'roomIds':
          return { ...field, options: roomsOptions, loading: isLoading };
        case 'educationalClassIds':
          return { ...field, options: educationalClassesOptions, loading: isLoading };
        default:
          return field;
      }
    });
  }, [teachersOptions, subjectsOptions, roomsOptions, educationalClassesOptions, isLoading]);

  const onFilterChange = useCallback(
    (values: Record<string, unknown>) => {
      dispatch(scheduleActions.setFilters(values as ScheduleFilters));
    },
    [dispatch],
  );

  return {
    state: {
      filterFields,
    },
    methods: {
      onFilterChange,
    },
  };
};
